import * as React from "react"
import { AlertCircle, Plus, Trash2 } from "lucide-react"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Button } from "@/components/ui/button"

interface ListEditorSheetProps<T> {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  /** Singular noun for an item, e.g. "credential", "instance". */
  itemNoun: string
  items: T[]
  newItem: () => T
  renderForm: (
    item: T,
    update: (patch: Partial<T>) => void,
    index: number,
    readOnly: boolean,
    isNew: boolean,
  ) => React.ReactNode
  /**
   * Called when the user clicks Done. Stages the edited list back to the
   * caller; the sheet does not persist on its own.
   */
  onSubmit: (next: T[]) => void
  /** When true, hides add/remove controls and shows a Close-only footer. */
  readOnly?: boolean
  /** Returns an error message when the staged list is invalid. */
  getValidationError?: (items: T[], newItemIndexes: ReadonlySet<number>) => string | undefined
}

export function ListEditorSheet<T>({
  open,
  onOpenChange,
  title,
  description,
  itemNoun,
  items,
  newItem,
  renderForm,
  onSubmit,
  readOnly = false,
  getValidationError,
}: ListEditorSheetProps<T>) {
  const [draft, setDraft] = React.useState<T[]>(items)
  const [newItemIndexes, setNewItemIndexes] = React.useState<Set<number>>(new Set())
  const [validationError, setValidationError] = React.useState<string>()
  const stagedNewItems = React.useRef(new WeakSet<object>())

  // Re-seed draft from the latest items only when the sheet (re)opens. The
  // parent panel may pass a new `items` array reference on every render, and
  // we don't want those to clobber in-progress sheet edits.
  React.useEffect(() => {
    if (open) {
      setDraft(items)
      setNewItemIndexes(new Set(
        items.flatMap((item, index) =>
          typeof item === "object" &&
          item !== null &&
          stagedNewItems.current.has(item)
            ? [index]
            : [],
        ),
      ))
      setValidationError(undefined)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const updateItem = (index: number, patch: Partial<T>) => {
    setDraft((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)))
    setValidationError(undefined)
  }

  const removeItem = (index: number) => {
    setDraft((prev) => prev.filter((_, i) => i !== index))
    setNewItemIndexes((prev) => new Set(
      [...prev]
        .filter((itemIndex) => itemIndex !== index)
        .map((itemIndex) => itemIndex > index ? itemIndex - 1 : itemIndex),
    ))
    setValidationError(undefined)
  }

  const addItem = () => {
    setNewItemIndexes((indexes) => new Set(indexes).add(draft.length))
    setDraft((prev) => [...prev, newItem()])
    setValidationError(undefined)
  }

  const handleDone = () => {
    const error = getValidationError?.(draft, newItemIndexes)
    if (error) {
      setValidationError(error)
      return
    }

    // Keep locally added entries marked as new if this sheet is reopened before
    // the outer form is saved. Persisted data arrives as new object instances,
    // so the marker is discarded automatically after the outer save refreshes.
    for (const index of newItemIndexes) {
      const item = draft[index]
      if (typeof item === "object" && item !== null) {
        stagedNewItems.current.add(item)
      }
    }

    onSubmit(draft)
    onOpenChange(false)
  }

  const handleCancel = () => {
    onOpenChange(false)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:w-[600px] sm:max-w-[600px] bg-card border-l border-border p-0 flex flex-col"
      >
        <SheetHeader className="px-6 py-5 border-b border-border">
          <SheetTitle className="text-lg font-semibold text-foreground">{title}</SheetTitle>
          {description && (
            <SheetDescription className="text-muted-foreground mt-1">
              {description}
            </SheetDescription>
          )}
        </SheetHeader>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {draft.length === 0 ? (
            <div className="border border-dashed border-border rounded-md py-10 text-center text-sm text-muted-foreground">
              No {itemNoun}s yet.
            </div>
          ) : (
            draft.map((item, index) => (
              <div
                key={index}
                className="border border-border rounded-md p-4 space-y-4 bg-muted/20"
              >
                <div className="flex items-center justify-between">
                  <h4 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {itemNoun} {index + 1}
                  </h4>
                  {!readOnly && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem(index)}
                      className="text-muted-foreground hover:text-destructive hover:bg-destructive/10 h-8 w-8"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">Remove {itemNoun}</span>
                    </Button>
                  )}
                </div>
                <div className="space-y-3">
                  {renderForm(
                    item,
                    (patch) => updateItem(index, patch),
                    index,
                    readOnly,
                    newItemIndexes.has(index),
                  )}
                </div>
              </div>
            ))
          )}
          {!readOnly && (
            <Button
              variant="outline"
              onClick={addItem}
              className="w-full bg-card border-input text-muted-foreground hover:bg-secondary hover:text-foreground"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add {itemNoun}
            </Button>
          )}
          {validationError && (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}
        </div>
        <SheetFooter className="px-6 py-4 border-t border-border flex-row justify-between gap-3">
          {readOnly ? (
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="bg-card border-input text-muted-foreground hover:bg-secondary hover:text-foreground"
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={handleCancel}
                className="bg-card border-input text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDone}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                Done
              </Button>
            </>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
